import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { ToastProvider } from '@/components/ui/Toast'
import { AppShell } from '@/components/layout/AppShell'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { HomePage } from '@/pages/HomePage'
import { DashboardPage } from '@/pages/DashboardPage'
import { HomeDashboardPage } from '@/pages/Home/HomeDashboardPage'
import { homeSurface } from '@/pages/dashboardRoute'
import { ConversationsPage } from '@/pages/ConversationsPage'
import { PersonasPage } from '@/pages/PersonasPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { LoginPage } from '@/pages/LoginPage'
import { ChangePasswordPage } from '@/pages/ChangePasswordPage'
import { UserManagementPage } from '@/pages/admin/UserManagementPage'
import { ServiceAccountsPage } from '@/pages/admin/ServiceAccountsPage'
import { PronunciationDictionariesPage } from '@/pages/admin/PronunciationDictionariesPage'
import { MailboxesPage } from '@/pages/admin/MailboxesPage'
import { PortabilityPage } from '@/pages/admin/PortabilityPage'
import { PersonaSharingPage } from '@/pages/admin/PersonaSharingPage'
import { CredentialSetsPage } from '@/pages/admin/CredentialSetsPage'
import { IntegrationsHubPage } from '@/pages/admin/integrations/IntegrationsHubPage'
import { WhatsAppSettingsPage } from '@/pages/admin/WhatsAppSettingsPage'
import { RoleManagementPage } from '@/pages/admin/RoleManagementPage'
import { WidgetManagementPage } from '@/pages/admin/WidgetManagementPage'
import { InboxPage } from '@/pages/InboxPage'
import { InboxDashboardPage } from '@/pages/InboxDashboardPage'
import { MarketingPage } from '@/pages/MarketingPage'
import { OrdersPage } from '@/pages/OrdersPage'
import { ProductCatalogPage } from '@/pages/admin/ProductCatalogPage'
import { BusinessPage } from '@/pages/admin/BusinessPage'
import { AppSettingsPage } from '@/pages/admin/AppSettingsPage'
import { AirportsPage } from '@/pages/admin/airports/AirportsPage'
import { CompaniesPage } from '@/pages/admin/companies/CompaniesPage'
import { RecordSettingsPage } from '@/pages/admin/record-settings/RecordSettingsPage'
import { QuoteDocumentPage } from '@/pages/admin/quote-document/QuoteDocumentPage'
import { PermissionOnly } from '@/components/auth/PermissionOnly'
import { AiModelsPage } from '@/pages/admin/ai-models/AiModelsPage'
import { ConversationAnalysisPage } from '@/pages/admin/conversation-analysis/ConversationAnalysisPage'
import { AnalysisRunPage } from '@/pages/admin/conversation-analysis/AnalysisRunPage'
import { FeedbackAdminPage } from '@/pages/admin/feedback/FeedbackAdminPage'
import { AdminArea, AdminIndex } from '@/pages/admin/AdminArea'
import { CrmPage } from '@/pages/CrmPage'
import { DisclaimerGate } from '@/components/disclaimer/DisclaimerGate'
import { GoogleOAuthCallback } from '@/pages/marketing/GoogleOAuthCallback'
import { PersonaWizard } from '@/pages/PersonaWizard'
import { PersonaWorkshop } from '@/pages/PersonaWorkshop'
import { ToolLibraryPage } from '@/pages/ToolLibrary'
import { AssetsPage } from '@/pages/AssetsPage'
import { DocsIndexPage } from '@/pages/docs/DocsIndexPage'
import { DocPage } from '@/pages/docs/DocPage'
import { WorkflowsPage } from '@/pages/WorkflowsPage'
import { WorkflowOverviewPage } from '@/pages/WorkflowOverview/WorkflowOverviewPage'
import { WorkflowSimulatePage } from '@/pages/WorkflowOverview/simulate/WorkflowSimulatePage'
import { ReplayLabPage, ReplayRunPage } from '@/pages/ReplayLab'
import { TasksPage } from '@/pages/TasksPage'
import { TaskDetailPage } from '@/pages/tasks/TaskDetailPage'
import { useAppInit, useMigration, useFeature } from '@/hooks'
import { usePermission } from '@/hooks/usePermission'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/authStore'
import { useFeatureStore } from '@/stores/featureStore'
import { SURFACES, SurfaceGuard, childSurfaces, FleetShell } from '@/custom'

/** Redirect users without admin:access permission to home. */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const canAccess = usePermission('admin:access')
  if (!canAccess) return <Navigate to="/" replace />
  return <>{children}</>
}

/** Old Visual Composer route — page deleted in increment #7; keep bookmarks working. */
export function ComposerRedirect() {
  const { personaId } = useParams()
  return <Navigate to={`/personas/${personaId}/workshop`} replace />
}

export function AuthenticatedApp() {
  // Initialize app with saved settings (only runs when authenticated)
  useAppInit()

  // Migrate localStorage conversations to backend (runs once)
  useMigration()

  const showInbox = useFeature('inbox')
  const showWhatsApp = useFeature('whatsapp')
  const showRbac = useFeature('rbac')
  const showMarketing = useFeature('marketing')
  const showCommerce = useFeature('commerce')
  const showWidget = useFeature('widget')
  const showCrm = useFeature('crm')
  const showPersonaWizard = useFeature('persona_wizard')
  const showPersonaWorkshop = useFeature('persona_workshop')
  const showToolFramework = useFeature('tool_framework')
  const showWorkflowTransparency = useFeature('workflow_transparency')
  const showReplayLab = useFeature('replay_lab')
  const showPortability = useFeature('portability')
  const showVoice = useFeature('voice')
  const showEmailIngestion = useFeature('email_ingestion')
  const showConversationAnalytics = useFeature('conversation_analytics')
  const showProductFeedback = useFeature('product_feedback')
  const showProcessEngine = useFeature('process_engine')
  const showWorkflowEngine = useFeature('workflow_engine')
  // SA-1366: the airports reference (and its admin page) is the aviation kit's.
  const showAviation = useFeature('aviation')
  const isFeatureEnabled = useFeatureStore((st) => st.isEnabled)
  // SA-1456 spec §7.2: the Companies page exists where a module uses companies (the manifest's
  // `companies_page`). Kept while the manifest loads so a deep link survives; the page itself waits
  // for both stores and sends anyone else home.
  const companiesRoute = useFeatureStore((st) => !st.isLoaded || st.companiesPage)

  // The first screen (Home spec 2026-09-23 D1): the widget page when the loaded
  // manifest holds `home_dashboard`; else the bakery dashboard for smb/full;
  // else the legacy Home.
  const profile = useFeatureStore((s) => s.profile)
  const featuresLoaded = useFeatureStore((s) => s.isLoaded)
  const features = useFeatureStore((s) => s.features)
  const surface = homeSurface(profile, features, featuresLoaded)

  return (
    <>
      <AppShell>
      <Routes>
        <Route path="/" element={surface === 'widgets' ? <HomeDashboardPage /> : surface === 'dashboard' ? <DashboardPage /> : <HomePage />} />
        <Route path="/conversations" element={<ConversationsPage />} />
        <Route path="/conversations/:conversationId" element={<ConversationsPage />} />
        <Route path="/personas" element={<PersonasPage />} />
        {showPersonaWizard && (
          <Route path="/personas/new/wizard" element={<PersonaWizard />} />
        )}
        {/* Old Visual Composer route → Workshop (page deleted in increment #7) */}
        <Route path="/personas/:personaId/composer" element={<ComposerRedirect />} />
        {/* Persona Workshop — only when persona_workshop enabled */}
        {showPersonaWorkshop && (
          <Route path="/personas/:personaId/workshop" element={<PersonaWorkshop />} />
        )}
        <Route path="/settings" element={<SettingsPage />} />
        {/* Help — always available; per-page visibility/feature gating handled in the registry */}
        <Route path="/help" element={<DocsIndexPage />} />
        <Route path="/help/*" element={<DocPage />} />
        {/* Inbox — only when inbox feature is enabled */}
        {showInbox && (
          <>
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/dashboard" element={<InboxDashboardPage />} />
            <Route path="/inbox/:conversationId" element={<InboxPage />} />
            <Route path="/assets" element={<AssetsPage />} />
          </>
        )}
        {/* Marketing — only when marketing feature is enabled */}
        {showMarketing && (
          <Route path="/marketing" element={<MarketingPage />} />
        )}
        {/* Google OAuth callback — popup page, no auth required */}
        <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
        {/* Records (the /orders page under the pack's noun) — every
            workflow_engine deployment since E4 (SA-389, spec D26); the page
            gates its own payment/production/delivery UI on commerce. */}
        {showWorkflowEngine && (
          <Route path="/orders" element={<OrdersPage />} />
        )}
        {/* CRM — only when crm feature is enabled */}
        {showCrm && (
          <Route path="/crm/*" element={<CrmPage />} />
        )}
        {/* Workflows library + overview — only when workflow_transparency feature is enabled */}
        {showWorkflowTransparency && (
          <>
            <Route path="/workflows" element={<WorkflowsPage />} />
            <Route path="/workflows/:workflowId" element={<WorkflowOverviewPage />} />
            <Route
              path="/workflows/:workflowId/simulate"
              element={<WorkflowSimulatePage />}
            />
          </>
        )}
        {/* Tasks — only when process_engine feature is enabled */}
        {showProcessEngine && (
          <>
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
          </>
        )}
        {/* Replay Lab — only when replay_lab feature is enabled */}
        {showReplayLab && (
          <>
            <Route path="/replay-lab" element={<ReplayLabPage />} />
            <Route path="/replay-lab/runs/:runId" element={<ReplayRunPage />} />
          </>
        )}
        {/* The admin area (SA-963, spec §4.1): one parent with no guard and no
            splat; its index has none either (SA-1115): AdminIndex opens the
            viewer's first row, else home. Every child keeps its own path, gate, AdminOnly and component,
            so deep links hold, a custom role keeps a surface's URL, and a
            gated-off or unknown /admin path matches nothing here and falls to
            the catch-all. */}
        <Route path="/admin" element={<AdminArea surfaces={SURFACES} />}>
          <Route index element={<AdminIndex />} />
          <Route path="app-settings" element={<AdminOnly><AppSettingsPage /></AdminOnly>} />
          {showAviation && (
            <Route path="airports" element={<AdminOnly><AirportsPage /></AdminOnly>} />
          )}
          {companiesRoute && (
            <Route path="companies" element={<PermissionOnly permission="companies:manage"><CompaniesPage /></PermissionOnly>} />
          )}
          {showWorkflowEngine && (
            <Route path="quote-document" element={<AdminOnly><QuoteDocumentPage /></AdminOnly>} />
          )}
          {showProcessEngine && (
            <Route path="record-settings" element={<PermissionOnly permission="orders:settings:manage"><RecordSettingsPage /></PermissionOnly>} />
          )}
          <Route path="ai-models" element={<PermissionOnly permission="ai_models:read"><AiModelsPage /></PermissionOnly>} />
          <Route path="users" element={<AdminOnly><UserManagementPage /></AdminOnly>} />
          <Route path="service-accounts" element={<AdminOnly><ServiceAccountsPage /></AdminOnly>} />
          {showVoice && (
            <Route path="pronunciation-dictionaries" element={<AdminOnly><PronunciationDictionariesPage /></AdminOnly>} />
          )}
          {showEmailIngestion && (
            <Route path="mailboxes" element={<AdminOnly><MailboxesPage /></AdminOnly>} />
          )}
          {showPortability && (
            <Route path="portability" element={<AdminOnly><PortabilityPage /></AdminOnly>} />
          )}
          <Route path="sharing" element={<AdminOnly><PersonaSharingPage /></AdminOnly>} />
          <Route path="credential-sets" element={<AdminOnly><CredentialSetsPage /></AdminOnly>} />
          <Route path="integrations" element={<AdminOnly><IntegrationsHubPage /></AdminOnly>} />
          {showRbac && (
            <Route path="roles" element={<AdminOnly><RoleManagementPage /></AdminOnly>} />
          )}
          {/* Admin Contacts page retired (CRM PR 5) — bookmarks land on the CRM list */}
          <Route path="contacts" element={<Navigate to="/crm/contacts" replace />} />
          {showWhatsApp && (
            <Route path="whatsapp" element={<AdminOnly><WhatsAppSettingsPage /></AdminOnly>} />
          )}
          {showWidget && (
            <Route path="widgets" element={<AdminOnly><WidgetManagementPage /></AdminOnly>} />
          )}
          {showCommerce && (
            <Route path="products" element={<AdminOnly><ProductCatalogPage /></AdminOnly>} />
          )}
          {showCommerce && (
            <Route path="business" element={<AdminOnly><BusinessPage /></AdminOnly>} />
          )}
          {showToolFramework && (
            <Route path="tools" element={<AdminOnly><ToolLibraryPage /></AdminOnly>} />
          )}
          {showConversationAnalytics && (
            <>
              <Route path="conversation-analysis" element={<AdminOnly><ConversationAnalysisPage /></AdminOnly>} />
              <Route path="conversation-analysis/runs/:runId" element={<AdminOnly><AnalysisRunPage /></AdminOnly>} />
            </>
          )}
          {showProductFeedback && (
            <Route path="feedback" element={<PermissionOnly permission="app_feedback:read"><FeedbackAdminPage /></PermissionOnly>} />
          )}
          {/* A module's admin-section surfaces (src/custom/index.ts): inside the
              area, SurfaceGuard-only as before. validateManifest keeps each under
              /admin/ with no children. */}
          {SURFACES.filter((s) => s.nav?.section === 'admin' && isFeatureEnabled(s.feature)).map((s) => (
            <Route
              key={s.id}
              path={s.route.slice('/admin/'.length)}
              element={<SurfaceGuard permission={s.permission}><s.Component /></SurfaceGuard>}
            />
          ))}
        </Route>
        {/* Customer-specific surfaces (src/custom/index.ts, SA-321 §7.1, SA-323 §7.1).
            Feature gate here; permission gate inside SurfaceGuard; every top-level
            entry renders through the shell, which carries its title (SA-1125) and
            its tabs when it has enabled children.
            Admin-section entries render inside the admin area, above. */}
        {SURFACES.filter((s) => !s.parent && s.nav?.section !== 'admin' && isFeatureEnabled(s.feature)).map((s) => {
          const kids = childSurfaces(s.id).filter((c) => isFeatureEnabled(c.feature))
          return (
            <Route key={s.id} path={`${s.route}/*`} element={<SurfaceGuard permission={s.permission}><FleetShell entry={s} /></SurfaceGuard>}>
              <Route index element={<SurfaceGuard permission={s.permission}><s.Component /></SurfaceGuard>} />
              {kids.map((c) => (
                <Route key={c.id} path={c.route.slice(s.route.length + 1)} element={<SurfaceGuard permission={c.permission}><c.Component /></SurfaceGuard>} />
              ))}
            </Route>
          )
        })}
        {/* Redirect old /demo route */}
        <Route path="/demo" element={<Navigate to="/" replace />} />
        {/* Catch-all (also catches disabled feature routes) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AppShell>
      <DisclaimerGate />
    </>
  )
}

function App() {
  const { tryRefresh } = useAuth()
  const isInitialized = useAuthStore((s) => s.isInitialized)
  const refreshAttempted = useRef(false)
  const loadFeatures = useFeatureStore((s) => s.loadFeatures)
  const featuresLoaded = useFeatureStore((s) => s.isLoaded)
  const featuresAttempted = useRef(false)

  // On mount, load feature manifest (public, no auth) in parallel with auth.
  useEffect(() => {
    if (!featuresLoaded && !featuresAttempted.current) {
      featuresAttempted.current = true
      loadFeatures()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // On mount, try to restore session from refresh token cookie.
  // The ref guard prevents StrictMode double-mount from firing two
  // concurrent refresh requests, which would trigger token-theft detection.
  useEffect(() => {
    if (!isInitialized && !refreshAttempted.current) {
      refreshAttempted.current = true
      tryRefresh()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ToastProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Auth required, but password change NOT enforced here */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />

        {/* All other routes require auth + password changed */}
        <Route
          path="/*"
          element={
            <ProtectedRoute requirePasswordChanged>
              <AuthenticatedApp />
            </ProtectedRoute>
          }
        />
      </Routes>
    </ToastProvider>
  )
}

export default App
